import { useEffect, useState } from "react";
import {
  deleteTerm as deleteTermFromAPI,
  fetchTerms,
  getTermsCount,
  updateTermInformation,
} from "../term/termAPI";
import { useLocation, useNavigate } from "react-router-dom";
import { logout } from "../auth/AuthManager";
import { ServerStatus, fetchServerStatus } from "./serverStatus";
import { useTranslation } from "react-i18next";
import styles from "./css/MainScreen.module.css";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../configurations/firebaseConfig";
import TermListScreen from "../term/view/TermListScreen";
import LoadingScreen from "./LoadingScreen";
import Term, { getNextLevel } from "../term/Term";
import {
  getSelectedGroupsFromLocalStorage,
  getSortTypeFromLocalStorage,
} from "../settings/util/termListSettings";
import { getRandomNumber } from "./numberManager";
import { Status } from "./Status";
import { TermLevel } from "../term/custom_view/TermLevel";
import { updateTermLevel as updateTermLevelFromAPI } from "../term/termAPI";
import { updateTermMark as updateTermMarkFromAPI } from "../term/termAPI";
import OnevocaDialog from "./OnevocaDialog";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import TermEditScreen from "../term/view/TermEditScreen";
import {
  updateTerm as updateTermFromAPI,
  updateTermsGroup as updateTermsGroupFromAPI,
  updateTermsLevel as updateTermsLevelFromAPI,
  deleteTerms as deleteTermsFromAPI,
} from "../term/termAPI";
import Toast from "../../components/ui/Toast";
import TermAddScreen from "../term/view/TermAddScreen";
import BusinessContainer from "./BusinessContainer";
import ContactUsModal from "./ContactUsModal";
import GroupSelectScreen from "../groups/GroupSelectScreen";
import Group from "../groups/Group";
import BasicMenu from "./BasicMenu";
import AccountMenu from "../auth/AccountMenu";
import { getSlots, updateUser } from "../auth/authAPI";
import {
  setMyTermsCountToLocalStorage,
  setSlotsToLocalStorage,
} from "../settings/util/userSettings";
import LearningSelectScreen from "../learning/view/LearningSelectScreen";
import { useAppDispatch } from "../../hook";
import { updateTerms } from "../term/termSlice";
import { useTerms } from "../term/useTerms";
import AppShell, { appRoutes } from "../../components/layout/AppShell";
import { updateLevel } from "./levelBadgeSlice";
import { makeLevelBadgeFrom } from "./levelBadge";
import CSVImportView from "../csv/view/CSVImportView";
import { getFilteredTermsWithOptions } from "../term/terms";
import { exportTermsCsv } from "../csv/util/csvExport";
import MarkedTermListScreen from "../term/view/MarkedTermList";
import MarkedTermLearningScreen from "../term/view/MarkedTermLearningScreen";
import LearningRetry from "../learning/model/LearningRetry";
import { LearningMode } from "../learning/model/LearningMode";
import { getLatestSaveGroup } from "../groups/utils/groupSettings";
import { TermSentenceWithStringValue } from "../sentence/model/Sentence";

export default function MainScreen() {
  const { t } = useTranslation();
  const location = useLocation();
  const tab = location.pathname.startsWith(appRoutes.learn)
    ? "learning"
    : "home";
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const termsDispatch = useAppDispatch();
  const [terms] = useTerms();

  const levelBadgeDispatch = useAppDispatch();

  const [serverStatus, setServerStatus] = useState<ServerStatus>();
  const [termFetchStatus, setTermFetchStatus] = useState(Status.Ready);
  const [termLevelUpdateStatus, setTermLevelUpdateStatus] = useState(
    Status.Ready
  );

  const [termUpdateStatus, setTermUpdateStatus] = useState(Status.Ready);

  const [termDeleteStatus, setTermDeleteStatus] = useState(Status.Ready);
  const [termsUpdateStatus, setTermsUpdateStatus] = useState(Status.Ready);

  const [termEditSelectedItem, setTermEditSelectedItem] = useState<Term | null>(
    null
  );

  const [termDeleteSelectedItem, setTermDeleteSelectedItem] =
    useState<Term | null>(null);
  const [termsGroupChangeSelected, setTermsGroupChangeSelected] = useState<
    Term[] | null
  >(null);
  const [termsLevelChangeSelected, setTermsLevelChangeSelected] = useState<
    Term[] | null
  >(null);
  const [termsDeleteSelected, setTermsDeleteSelected] = useState<Term[] | null>(
    null
  );

  const [showingMarkedTermListScreen, setShowingMarkedTermListScreen] =
    useState(false);
  const [showingMarkedTermLearningScreen, setShowingMarkedTermLearningScreen] =
    useState(false);
  const [showTermAddScreen, setShowTermAddScreen] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [openLogOutConfirmDialog, setOpenLogOutConfirmDialog] =
    useState(false);
  const [showContactUs, setShowContactUs] = useState(false);
  const [openCSVImport, setOpenCSVImport] = useState(false);
  const [openCSVExportGroupSelect, setOpenCSVExportGroupSelect] =
    useState(false);

  const navigate = useNavigate();

  const selectedLevelButtons = [
    {
      title: t("Difficult"),
      handleOnClose: () => {
        handleOnCloseSelectedLevelMenu(TermLevel.difficult);
      },
    },
    {
      title: t("Familiar"),
      handleOnClose: () => {
        handleOnCloseSelectedLevelMenu(TermLevel.familiar);
      },
    },
    {
      title: t("Perfect"),
      handleOnClose: () => {
        handleOnCloseSelectedLevelMenu(TermLevel.perfect);
      },
    },
  ];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        checkServerInspect();
      } else {
        logout();
        navigate("/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [tab]);

  useEffect(() => {
    if (termEditSelectedItem !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [termEditSelectedItem]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;

    if (showTermAddScreen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [showTermAddScreen]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;

    if (showingMarkedTermListScreen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [showingMarkedTermListScreen]);

  useEffect(() => {
    if (serverStatus !== undefined && serverStatus !== null) {
      switch (serverStatus) {
        case ServerStatus.Maintenance:
          navigate("/maintenance");
          break;
        case ServerStatus.Running:
          updateUser();
          updateSlot();
          updateTermsCount();
          fetchData(false);
          break;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverStatus]);

  useEffect(() => {
    switch (termFetchStatus) {
      case Status.Ready:
        setIsLoading(false);
        break;
      case Status.Loading:
        setIsLoading(true);
        break;
      case Status.Done:
        setIsLoading(false);
        break;
    }
  }, [termFetchStatus]);

  useEffect(() => {
    switch (termLevelUpdateStatus) {
      case Status.Ready:
        break;
      case Status.Loading:
        break;
      case Status.Done:
        break;
    }
  }, [termLevelUpdateStatus]);

  useEffect(() => {
    switch (termUpdateStatus) {
      case Status.Ready:
        setIsLoading(false);
        break;
      case Status.Loading:
        setIsLoading(true);
        break;
      case Status.Done:
        setIsLoading(false);
        setToastMessage(t("TermEditedToastMessage"));
        break;
    }
  }, [t, termUpdateStatus]);

  useEffect(() => {
    switch (termDeleteStatus) {
      case Status.Ready:
        setIsLoading(false);
        break;
      case Status.Loading:
        setIsLoading(true);
        break;
      case Status.Done:
        setIsLoading(false);
        break;
    }
  }, [termDeleteStatus]);

  useEffect(() => {
    switch (termsUpdateStatus) {
      case Status.Ready:
        setIsLoading(false);
        break;
      case Status.Loading:
        setIsLoading(true);
        break;
      case Status.Done:
        setIsLoading(false);
        setToastMessage(t("TermEditedToastMessage"));
        break;
    }
  }, [t, termsUpdateStatus]);

  useEffect(() => {}, [terms]);

  async function updateSlot() {
    try {
      const slot = await getSlots();
      setSlotsToLocalStorage(slot);
    } catch (error) {
      console.log(error);
      // iOS(SlotDataManager.applySlot)와 동일하게 실패 시 기본 슬롯 50
      setSlotsToLocalStorage("50");
    }
  }

  async function updateTermsCount() {
    try {
      const termsCount = await getTermsCount();
      const level = makeLevelBadgeFrom(termsCount);
      levelBadgeDispatch(updateLevel(level));
      setMyTermsCountToLocalStorage(termsCount.toString());
    } catch (error) {
      console.log(error);
    }
  }

  async function checkServerInspect() {
    setIsLoading(true);
    try {
      setServerStatus(await fetchServerStatus());
      setIsLoading(false);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      }
      setIsLoading(false);
    }
  }

  async function fetchData(withShuffle: boolean) {
    const sort = withShuffle ? undefined : getSortTypeFromLocalStorage();
    const seed = withShuffle ? getRandomNumber(1, 999999) : 1;
    try {
      setTermFetchStatus(Status.Loading);
      const newTerms = await fetchTerms(seed, sort);
      const serializedTerms = newTerms.map((term) => Term.toJson(term));
      termsDispatch(updateTerms(serializedTerms));
      setTermFetchStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setTermFetchStatus(Status.Ready);
    }
  }

  function handleOpenAccountMenu(anchor: HTMLElement) {
    setAnchorEl(anchor);
    setShowAccountMenu(true);
  }

  function handleOnClickSlotAddButton() {
    navigate("/slot-purchase");
  }

  function handleOnClickAccountSettingsButton() {
    navigate("/account-settings");
  }

  function handleOnClickCSVImportButton() {
    setShowAccountMenu(false);
    setOpenCSVImport(true);
  }

  function handleOnClickCSVExportButton() {
    setShowAccountMenu(false);
    setOpenCSVExportGroupSelect(true);
  }

  // 바로 로그아웃하지 않고 확인을 받는다
  function handleClickLogOutButton() {
    setShowAccountMenu(false);
    setOpenLogOutConfirmDialog(true);
  }

  async function confirmLogOut() {
    setOpenLogOutConfirmDialog(false);
    await logout();
  }

  function handleOnClickMarkListButton() {
    setShowingMarkedTermListScreen(true);
  }

  async function handleOnClickTermMark(term: Term) {
    try {
      const newMark = !term.isMarked;
      updateTermMarkFromAPI(term, newMark);
      updateTermMarkToList(term, newMark);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  async function handleOnClickTermLevel(term: Term) {
    setTermLevelUpdateStatus(Status.Loading);
    try {
      const nextLevel = getNextLevel(term.level);
      updateTermLevelFromAPI(term, nextLevel);
      updateTermLevelToList(term, nextLevel);
      setTermLevelUpdateStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setTermLevelUpdateStatus(Status.Ready);
    }
  }

  async function updateMemo(item: TermSentenceWithStringValue) {
    try {
      await updateTermInformation(
        item.term.id,
        undefined,
        undefined,
        item.stringValue,
        undefined
      );
      updateTermMemoToList(item);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  async function updateTerm(term: Term) {
    setTermUpdateStatus(Status.Loading);
    try {
      await updateTermFromAPI(term);
      updateTermToList(term);
      setTermUpdateStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setTermUpdateStatus(Status.Ready);
    }
  }

  function updateTermToList(term: Term) {
    const termIndex = terms.findIndex((t) => t.id === term.id);
    if (termIndex !== -1) {
      const newTerms = [...terms];
      newTerms[termIndex] = term;
      const serializedTerms = newTerms.map((term) => Term.toJson(term));
      termsDispatch(updateTerms(serializedTerms));
    }
  }

  function updateTermMarkToList(term: Term, isOn: boolean) {
    const termIndex = terms.findIndex((t) => t.id === term.id);
    if (termIndex !== -1) {
      const newTerms = [...terms];
      newTerms[termIndex] = {
        ...newTerms[termIndex],
        isMarked: isOn,
      };
      const serializedTerms = newTerms.map((term) => Term.toJson(term));
      termsDispatch(updateTerms(serializedTerms));
    }
  }

  function updateTermLevelToList(term: Term, toLevel: TermLevel) {
    const termIndex = terms.findIndex((t) => t.id === term.id);
    if (termIndex !== -1) {
      const newTerms = [...terms];
      newTerms[termIndex] = {
        ...newTerms[termIndex],
        level: toLevel,
      };
      const serializedTerms = newTerms.map((term) => Term.toJson(term));
      termsDispatch(updateTerms(serializedTerms));
    }
  }

  function updateTermMemoToList(item: TermSentenceWithStringValue) {
    const termIndex = terms.findIndex((t) => t.id === item.term.id);
    if (termIndex !== -1) {
      const newTerms = [...terms];
      newTerms[termIndex] = {
        ...newTerms[termIndex],
        memo: item.stringValue,
      };
      const serializedTerms = newTerms.map((term) => Term.toJson(term));
      termsDispatch(updateTerms(serializedTerms));
    }
  }

  function handleOnClickTermEditButton(term: Term) {
    setTermEditSelectedItem(term);
  }

  function handleOnClickTermDeleteButton(term: Term) {
    setTermDeleteSelectedItem(term);
  }

  function handleOnClickTermAddButton() {
    setShowTermAddScreen(true);
  }

  function handleOnClickShuffleButton() {
    fetchData(true);
  }

  function handleOnClickSelectAllButton() {
    updateSelectionsOfTerms(true);
  }

  function handleOnClickCancelSelectionAllButton() {
    updateSelectionsOfTerms(false);
  }

  function handleOnCloseTermAddScreen() {
    setShowTermAddScreen(false);
  }

  function handleOnClickSelectedTermsGroupChangeButton(selectedTerms: Term[]) {
    setTermsGroupChangeSelected(selectedTerms);
  }

  function handleOnClickSelectedTermsLevelChangeButton(
    e: React.MouseEvent<HTMLDivElement>,
    selectedTerms: Term[]
  ) {
    setAnchorEl(e.currentTarget);
    setTermsLevelChangeSelected(selectedTerms);
  }

  function handleOnClickSelectedTermsDeleteButton(selectedTerms: Term[]) {
    setTermsDeleteSelected(selectedTerms);
  }

  async function exportCSVTerms(groups: Group[]) {
    const currentDate = new Date();
    const dateString = currentDate.toISOString().split("T")[0];

    const termsToExport = getFilteredTermsWithOptions(
      terms,
      undefined,
      undefined,
      groups,
      undefined
    );
    exportTermsCsv(termsToExport, {
      filename: `${dateString}.csv`,
      encoding: "utf-8",
      includeBom: true,
      delimiter: ",",
      quoteAll: false,
      newline: "\r\n",
    });
  }

  async function deleteTermsServer(deleteTerms: Term[]) {
    setTermsUpdateStatus(Status.Loading);
    try {
      await deleteTermsFromAPI(deleteTerms);
      deleteTermsToList(deleteTerms);
      setTermsUpdateStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setTermsUpdateStatus(Status.Ready);
    }
  }

  function deleteTermsToList(deleteTerms: Term[]) {
    const idsToDelete = new Set(deleteTerms.map((term) => term.id));
    const newTerms = terms.filter((term) => !idsToDelete.has(term.id));
    const serializedTerms = newTerms.map((term) => Term.toJson(term));
    termsDispatch(updateTerms(serializedTerms));
  }

  async function deleteTermServer(term: Term) {
    if (term === null) {
      return;
    }

    setTermDeleteStatus(Status.Loading);
    try {
      await deleteTermFromAPI(term);
      deleteTerm(term);
      setTermDeleteStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setTermDeleteStatus(Status.Ready);
    }
  }

  function deleteTerm(term: Term) {
    const termIndex = terms.findIndex((t) => t.id === term.id);
    const newTerms = [...terms];
    if (termIndex !== -1) {
      newTerms.splice(termIndex, 1);
    }
    const serializedTerms = newTerms.map((term) => Term.toJson(term));
    termsDispatch(updateTerms(serializedTerms));
  }

  function updateSelectionsOfTerms(selection: boolean) {
    const newTerms = terms.map((term) => ({
      ...term,
      selected: selection,
    }));
    const serializedTerms = newTerms.map((term) => Term.toJson(term));
    termsDispatch(updateTerms(serializedTerms));
  }

  async function updateTermsLevel(toTerms: Term[], toLevel: TermLevel) {
    try {
      setTermsUpdateStatus(Status.Loading);
      await updateTermsLevelFromAPI(toTerms, toLevel);
      updateTermsLevelToList(toTerms, toLevel);
      setTermsUpdateStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setTermUpdateStatus(Status.Ready);
    }
  }

  function updateTermsLevelToList(toTerms: Term[], toLevel: TermLevel) {
    const newTerms = terms.map((term) => {
      const matchingTerm = toTerms.find((toTerm) => toTerm.id === term.id);
      if (matchingTerm) {
        return {
          ...term,
          level: toLevel,
        };
      }
      return term;
    });
    const serializedTerms = newTerms.map((term) => Term.toJson(term));
    termsDispatch(updateTerms(serializedTerms));
  }

  async function updateTermsGroup(toTerms: Term[], toGroup: Group) {
    try {
      setTermsUpdateStatus(Status.Loading);
      await updateTermsGroupFromAPI(toTerms, toGroup);
      updateTermsGroupToList(toTerms, toGroup);
      setTermsUpdateStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setTermsUpdateStatus(Status.Ready);
    }
  }

  function updateTermsGroupToList(toTerms: Term[], toGroup: Group) {
    const newGroup = toGroup.isUnspecifiedGroup ? "" : toGroup.title;
    const newTerms = terms.map((term) => {
      const matchingTerm = toTerms.find((toTerm) => toTerm.id === term.id);
      if (matchingTerm) {
        return {
          ...term,
          group: newGroup,
        };
      }
      return term;
    });
    const serializedTerms = newTerms.map((term) => Term.toJson(term));
    termsDispatch(updateTerms(serializedTerms));
  }

  function handleOnCloseSelectedLevelMenu(level: TermLevel) {
    if (termsLevelChangeSelected !== null) {
      updateTermsLevel(termsLevelChangeSelected, level);
    }
    setTermsLevelChangeSelected(null);
  }

  function handleOnClickContactUsButton() {
    setShowAccountMenu(false);
    setShowContactUs(true);
  }

  function getLatestSelectedGroup(): Group | undefined {
    const selectedGroups = getSelectedGroupsFromLocalStorage();
    const latestSaveGroup = getLatestSaveGroup();

    if (selectedGroups.length === 0) {
      return latestSaveGroup === null ? undefined : latestSaveGroup;
    }

    if (selectedGroups.length === 1) {
      const selectedGroup = selectedGroups[0];
      if (selectedGroup.isAllGroup || selectedGroup.isUnspecifiedGroup) {
        return latestSaveGroup === null ? undefined : latestSaveGroup;
      }
    }

    return selectedGroups[0];
  }

  return (
    <AppShell
      onOpenAccount={handleOpenAccountMenu}
      accountOpen={showAccountMenu}
      overlays={
        <>
          {isLoading === true && <LoadingScreen />}
          <TermAddScreen
            open={showTermAddScreen === true}
            selectedGroup={getLatestSelectedGroup()}
            onSaved={() => {
              setToastMessage(t("TermRegisrered"));
              fetchData(false);
            }}
            onClose={() => handleOnCloseTermAddScreen()}
          />
          <TermEditScreen
            open={termEditSelectedItem !== null}
            term={termEditSelectedItem!}
            handleOnSave={(term: Term) => updateTerm(term)}
            handleOnClose={() => {
              setTermEditSelectedItem(null);
            }}
          />
          <MarkedTermListScreen
            open={showingMarkedTermListScreen}
            handleOnClickMarkButton={handleOnClickTermMark}
            handleOnClickLevelButton={handleOnClickTermLevel}
            handleOnClickTermEditButton={handleOnClickTermEditButton}
            handleOnClickTermDeleteButton={handleOnClickTermDeleteButton}
            handleOnClickCopySentenceToMemoButton={updateMemo}
            onClickLearnButton={() => {
              setShowingMarkedTermListScreen(false);
              setShowingMarkedTermLearningScreen(true);
            }}
            onClose={() => {
              setShowingMarkedTermListScreen(false);
            }}
          />
          <MarkedTermLearningScreen
            open={showingMarkedTermLearningScreen}
            onClickStartButton={(data: LearningRetry) => {
              navigate("/learning", {
                state: {
                  defaultTerms: data.terms,
                  learningType: data.type,
                  learningMode: LearningMode.restartFromLearningHistory,
                  learningContinue: null,
                },
              });
            }}
            onClose={() => {
              setShowingMarkedTermLearningScreen(false);
            }}
          />
          <GroupSelectScreen
            open={openCSVExportGroupSelect}
            selectStyle="multiple"
            showAllGroup={true}
            showNoGroup={true}
            showGroupAddButton={false}
            handleOnSelectGroups={exportCSVTerms}
            handleOnClose={() => {
              setOpenCSVExportGroupSelect(false);
            }}
          />
          <GroupSelectScreen
            open={termsGroupChangeSelected !== null}
            selectStyle="single"
            showAllGroup={false}
            showNoGroup={false}
            showGroupAddButton={true}
            handleOnSelectGroups={(groups: Group[]) => {
              const group = groups[0] ?? null;
              if (group !== null && termsGroupChangeSelected !== null) {
                updateTermsGroup(termsGroupChangeSelected, group);
              }
            }}
            handleOnClose={() => {
              setTermsGroupChangeSelected(null);
            }}
          />
          <BasicMenu
            isOpen={termsLevelChangeSelected !== null}
            anchorEl={anchorEl}
            buttons={selectedLevelButtons}
            handleOnClose={() => {
              setTermsLevelChangeSelected(null);
            }}
          />
          <OnevocaDialog
            open={termsDeleteSelected !== null}
            title={null}
            message={t("areyousuremesssage")}
            buttons={[
              {
                title: t("Delete"),
                style: "solid",
                onClick: () => {
                  if (termsDeleteSelected !== null) {
                    const deleteTerms = [...termsDeleteSelected];
                    setTermsDeleteSelected(null);
                    deleteTermsServer(deleteTerms);
                  }
                },
              },
              {
                title: t("Cancel"),
                style: "normal",
                onClick: () => {
                  setTermsDeleteSelected(null);
                },
              },
            ]}
          />
          <AccountMenu
            open={showAccountMenu === true}
            anchorEl={anchorEl}
            onClickSlotAddButton={() => handleOnClickSlotAddButton()}
            onClickCSVImportButton={() => handleOnClickCSVImportButton()}
            onClickCSVExportButton={handleOnClickCSVExportButton}
            onClickSettingsButton={() => handleOnClickAccountSettingsButton()}
            onClickLogOutButton={() => handleClickLogOutButton()}
            onClickContactUsButton={() => handleOnClickContactUsButton()}
            onClose={() => {
              setShowAccountMenu(false);
            }}
          />
          <ConfirmDialog
            open={openLogOutConfirmDialog}
            title={t("SignOut")}
            message={t("SignOutConfirmMessage")}
            onClose={() => setOpenLogOutConfirmDialog(false)}
            actions={[
              {
                label: t("Cancel"),
                variant: "ghost",
                onClick: () => setOpenLogOutConfirmDialog(false),
              },
              {
                label: t("SignOut"),
                variant: "danger",
                onClick: () => confirmLogOut(),
              },
            ]}
          />
          <CSVImportView
            open={openCSVImport}
            onSaveNewTerms={() => {
              fetchData(false);
            }}
            onClose={() => {
              setOpenCSVImport(false);
            }}
          />
          <OnevocaDialog
            open={termDeleteSelectedItem !== null}
            title={null}
            message={t("areyousuremesssage")}
            buttons={[
              {
                title: t("Delete"),
                style: "solid",
                onClick: () => {
                  const term = termDeleteSelectedItem;
                  if (term !== null) {
                    setTermDeleteSelectedItem(null);
                    deleteTermServer(term);
                  }
                },
              },
              {
                title: t("Cancel"),
                style: "normal",
                onClick: () => {
                  setTermDeleteSelectedItem(null);
                },
              },
            ]}
          />
          <ContactUsModal
            open={showContactUs}
            onClose={() => setShowContactUs(false)}
          />
          <Toast
            open={toastMessage !== null}
            onClose={() => {
              setToastMessage(null);
            }}
            message={toastMessage}
          />
        </>
      }
    >
      <div className={styles.contents}>
        {tab === "home" && (
          <TermListScreen
            isLoading={isLoading}
            handleOnClickMarkListButton={handleOnClickMarkListButton}
            handleOnClickTermMarkButton={handleOnClickTermMark}
            handleOnClickLevelButton={handleOnClickTermLevel}
            handleOnClickTermEditButton={handleOnClickTermEditButton}
            handleOnClickTermDeleteButton={handleOnClickTermDeleteButton}
            handleOnClickTermAddButton={handleOnClickTermAddButton}
            handleOnClickShuffleButton={handleOnClickShuffleButton}
            handleOnClickSelectAllButton={() => handleOnClickSelectAllButton()}
            handleOnClickCancelSelectionAllButton={() =>
              handleOnClickCancelSelectionAllButton()
            }
            handleOnClickSelectedTermsGroupChangeButton={
              handleOnClickSelectedTermsGroupChangeButton
            }
            handleOnClickSelectedTermsLevelChangeButton={
              handleOnClickSelectedTermsLevelChangeButton
            }
            handleOnClickSelectedTermsDeleteButton={
              handleOnClickSelectedTermsDeleteButton
            }
            handleOnClickCopySentenceToMemoButton={updateMemo}
          />
        )}
        {tab === "learning" && <LearningSelectScreen />}
      </div>
      <BusinessContainer />
    </AppShell>
  );
}
